/**
 * Supporting Documents card for NCR Edit — list, upload, download, delete.
 *
 * Moved out of Sections.tsx (Customer review 11 Sep, drag and drop everywhere)
 * when its upload area became the shared FileDropZone, to bring Sections
 * under the 500-line limit. Behaviour is otherwise unchanged.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button, buttonVariants } from '../../components/ui/button';
import { FileText, Trash2, Loader2, Download } from 'lucide-react';
import { NCRDocument, qualityAPI } from '../../lib/api';
import { toast } from 'sonner';
import { useFormatters } from '../../contexts/PreferencesContext';
import FileDropZone from '../../components/common/FileDropZone';
import FileDownloadLink from '../../components/common/FileDownloadLink';

const ACCEPTED_FILE_TYPES = '.png,.jpg,.jpeg,.tif,.bmp,.gif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.pdf,.svg,.mp4,.msg';
const MAX_FILE_SIZE_MB = 10;

interface DocumentsSectionProps {
  ncrId: number;
  documents: NCRDocument[];
  onDocumentsChanged: () => void;
  /** B9: attach into one process's section (e.g. 'collection'); blank = general. */
  section?: string;
  title?: string;
  /** Render inside another card (the Collection card) rather than as its own. */
  embedded?: boolean;
}

export function DocumentsSection({
  ncrId, documents, onDocumentsChanged, section = '', title = 'Supporting Documents', embedded = false,
}: DocumentsSectionProps) {
  const [uploading, setUploading] = useState(false);
  const { formatDate } = useFormatters();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Customer review 11 Sep (drag and drop everywhere): the size cap and the
  // accepted types are enforced by FileDropZone for drops and picks alike.
  const handleUpload = async (files: File[]) => {
    for (const file of files) {
      try {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('ncr', String(ncrId));
        if (section) formData.append('section', section);
        formData.append('file_name', file.name);
        formData.append('file_size', String(file.size));
        formData.append('file_type', file.type || 'application/octet-stream');
        await qualityAPI.uploadNCRDocument(formData);
        toast.success(`Uploaded "${file.name}"`);
      } catch (error) {
        console.error('Error uploading document:', error);
        toast.error(`Failed to upload "${file.name}"`);
      } finally {
        setUploading(false);
      }
    }
    onDocumentsChanged();
  };

  const handleDelete = async (docId: number, fileName: string) => {
    try {
      setDeletingId(docId);
      await qualityAPI.deleteNCRDocument(docId);
      toast.success(`Deleted "${fileName}"`);
      onDocumentsChanged();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  const body = (
    <>
        {/* Upload area */}
        <FileDropZone
          multiple
          accept={ACCEPTED_FILE_TYPES}
          maxSize={MAX_FILE_SIZE_MB * 1024 * 1024}
          busy={uploading}
          hint={`Max ${MAX_FILE_SIZE_MB}MB per file. Supported: images, documents, PDFs, videos`}
          onFiles={handleUpload}
          data-testid={section ? `ncr-${section}-documents-drop-zone` : 'ncr-documents-drop-zone'}
        />

        {/* Document list */}
        {documents.length === 0 ? (
          <p className="text-muted-foreground text-center py-2">No documents attached yet</p>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-3 border rounded">
                <div className="flex-1 min-w-0">
                  {/* CR-5540: file name is now a link to the new download
                      endpoint so users can actually open uploaded files.
                      Falls back to plain text when the BE didn't surface
                      a download URL (legacy rows / file_path-only). */}
                  {doc.download_url ? (
                    <FileDownloadLink
                      url={doc.download_url}
                      fallbackName={doc.file_name}
                      className="font-medium truncate text-primary hover:underline block text-left disabled:opacity-60 disabled:cursor-not-allowed"
                      title="Download attachment"
                    >
                      {doc.file_name}
                    </FileDownloadLink>
                  ) : (
                    <p className="font-medium truncate">{doc.file_name}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {doc.uploaded_by_details?.first_name
                      ? `${doc.uploaded_by_details.first_name} ${doc.uploaded_by_details.last_name || ''}`.trim()
                      : doc.uploaded_by_details?.username || 'Unknown'}
                    {' · '}
                    {formatDate(doc.uploaded_at)}
                    {' · '}
                    {(doc.file_size / 1024).toFixed(0)} KB
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {doc.download_url && (
                    <FileDownloadLink
                      url={doc.download_url}
                      fallbackName={doc.file_name}
                      icon={<Download className="h-4 w-4" />}
                      title="Download"
                      aria-label={`Download ${doc.file_name}`}
                      className={buttonVariants({ variant: 'ghost', size: 'icon', className: 'h-8 w-8' })}
                    />
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive h-8 w-8"
                    onClick={() => handleDelete(doc.id, doc.file_name)}
                    disabled={deletingId === doc.id}
                  >
                    {deletingId === doc.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
    </>
  );

  if (embedded) {
    return (
      <div className="border-t pt-4 space-y-4">
        <h4 className="font-medium text-sm flex items-center gap-2">
          <FileText className="h-4 w-4" /> {title} ({documents.length})
        </h4>
        {body}
      </div>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {title} ({documents.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">{body}</CardContent>
    </Card>
  );
}
